/**
 * @module YMaps
 */

import Component from '../base/Component';
import { isTouch } from '../../scripts/helpers';
import './styles.css';

export * from './ymapsItems';

/**
 * Компонент Яндекс карт.
 * Версия API Яндекс карт: 2.1
 *
 * За основу был взят код нашего недоработанного плагина
 * Использовался плагин ymaps-list, версия 1.18.2
 * @see https://github.com/daer-ru/ymaps-list
 * @see https://www.npmjs.com/package/ymaps-list
 *
 * ВАЖНО! Внёс изменения в код плагина
 * 1. Убрал modernizr: this.touch = globalOptions.isTouch;
 * 2. При скролле к элементу, юзаем сам список, а не его обертку: const $list = $listContainer.find('.ylist-list');
 * 3. Добавил возможность устанавливать остальные опции метки из ymaps api: this.options.placemark.options = {}; // например, placemark.options = { hideIconOnBalloonOpen: true }
 * 4. switchContainer теперь не только строка. Его можно записать объектом. В таком случае запись будет вида: {id, type}. Это необходимо для реализации переключения "карта-список" на десктопе.
 * 4.1. Теперь контейнерам при переключении "карта-список" добавляется класс .is-map-visible (.is-map-hidden).
 * 4.2. Исправил ошибку _switchHandler. До этого обработчик события не снимался с помощью .off, так как на .on вешалась обертка над методом _switchHandler, а не сам метод: $(document).on('click', `#${switchId} [data-ylist-switch]`, switchHandler);
 * 5. Исправил баг с появлением пустого балуна: placemarkOptions.hasBalloon = false;
 * 6. Добавил возможность использовать функцию при определении иконки метки, на случай, если они разные: "else if (typeof iconGetter === 'function') {...}"
 * 7. Добавил статический метод, возвращающий необходимый экземпляр карты: static getInstance
 * 8. Исправил баг с схлопыванием меток в связке двух карт (если фильтруется маленькая карта, большая, связанная с ней, не должна фильтроваться): $(`#${this.options.container} #${placemarkItem.id}`).hide();
 * 9. Добавил свойство options.list.destroyOnSmall. Если options.list.destroyOnSmall == true, то на смартфонах список не отображается, карта показывается.
 * 10. Сделал рефакторинг кода под eslint
 * 11. Добавил параметр options.list.dataOrder (по аналогии с общим dataOrder), так как не всегда если необходимость добавилять в элемент списка сразу все элементы общего dataOrder. Параметр options.list.dataOrder необязательный
 * 12. Сделал чуть более гибкую настройку options.list.clickElement (теперь можно вводить селектор)
 */
export default class YMaps extends Component {
    static className = 'ylist';

    /**
     * Устанавливаем все свойства и методы класса
     *
     * @param {Object} options - параметры карты
     */
    setProperties(options = {}) {
        this.options = options;

        // Временное решение, в будущем, необходимо переписать все вызовы this.options.container на this.$block (напрямую)
        this.options.container = this.$block.attr('id');
        this.map = null;

        if (!Object.prototype.hasOwnProperty.call(options, 'data')) {
            this.points = null;
        } else {
            this.points = $.extend(true, [], this.options.data);
        }

        this.placemarks = [];
        this.activePlacemark = null;
        this.activeListItem = null;
        this.clusterer = null;
        this.balloonLayout = null;
        this.balloonParams = {
            balloonWidth: null,
            balloonHeight: null,
            balloonTailHeight: 15,
        };
        this.listClassName = 'ylist-list';
        this.touch = isTouch();
        this.isLessThanAdaptiveBreakpoint = false;
        this.options.adaptiveBreakpoint = this.options.adaptiveBreakpoint || 1024;
        this.mqlAdaptiveBreakpoint = window.matchMedia(`(max-width: ${this.options.adaptiveBreakpoint - 1}px)`);
        this.needReloadMap = true;

        this.currentFilterCallback = null;
        this.currentFilterParam = null; // параметр, по которому произошла последняя фильтрация

        this.adaptiveHandle.bind(this);
    }

    /**
     * Проверяет наличие всех обязательных параметров, устанавливает дефольные значения
     */
    checkRequiredOptions() {
        if (!this.points) {
            throw new Error('You need to JSON data');
        }

        if (
            !Object.prototype.hasOwnProperty.call(this.options, 'dataOrder') ||
            (Object.prototype.hasOwnProperty.call(this.options, 'dataOrder') && this.options.dataOrder.length == 0)
        ) {
            throw new Error('You need to set dataOrder option');
        }

        if (!Object.prototype.hasOwnProperty.call(this.options, 'dataExtension')) {
            this.options.dataExtension = {};
        }

        if (!Object.prototype.hasOwnProperty.call(this.options, 'afterInit')) {
            this.options.afterInit = false;
        }

        // Map
        if (!Object.prototype.hasOwnProperty.call(this.options, 'map')) {
            this.options.map = {};
        }

        if (Object.prototype.hasOwnProperty.call(this.options, 'map') && typeof this.options.map == 'object') {
            if (!Object.prototype.hasOwnProperty.call(this.options.map, 'center')) {
                throw new Error('You need to set map.center option');
            }

            if (!this.options.map.container) {
                // Если id не передается из параметров, его берём из шаблона
                const $mapContainer = this.$block.find('.ylist__map-container');

                if (!$mapContainer.length) {
                    throw new Error('You need to set map.container option');
                } else {
                    this.options.map.container = $mapContainer.attr('id');
                }
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.map, 'customize')) {
                this.options.map.customize = false;
            }

            if (
                Object.prototype.hasOwnProperty.call(this.options.map, 'customize') &&
                typeof this.options.map.customize == 'object'
            ) {
                if (!Object.prototype.hasOwnProperty.call(this.options.map.customize, 'state')) {
                    this.options.map.customize.state = false;
                }

                if (!Object.prototype.hasOwnProperty.call(this.options.map.customize, 'options')) {
                    this.options.map.customize.options = {};
                }

                if (!Object.prototype.hasOwnProperty.call(this.options.map.customize, 'controls')) {
                    this.options.map.customize.controls = false;
                }
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.map, 'drag')) {
                this.options.map.drag = {};
            }

            if (
                Object.prototype.hasOwnProperty.call(this.options.map, 'drag') &&
                typeof this.options.map.drag == 'object'
            ) {
                if (!Object.prototype.hasOwnProperty.call(this.options.map.drag, 'disableOnTouch')) {
                    this.options.map.drag.disableOnTouch = true;
                }

                if (!Object.prototype.hasOwnProperty.call(this.options.map.drag, 'disableAlways')) {
                    this.options.map.drag.disableAlways = false;
                }
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.map, 'dragTooltip')) {
                this.options.map.dragTooltip = {};
            }

            if (
                Object.prototype.hasOwnProperty.call(this.options.map, 'dragTooltip') &&
                typeof this.options.map.dragTooltip == 'object'
            ) {
                if (!Object.prototype.hasOwnProperty.call(this.options.map.dragTooltip, 'active')) {
                    this.options.map.dragTooltip.active = true;
                }

                if (!Object.prototype.hasOwnProperty.call(this.options.map.dragTooltip, 'text')) {
                    this.options.map.dragTooltip.text = 'To drag map touch screen by two fingers and move';
                }
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.map, 'filterTooltip')) {
                this.options.map.filterTooltip = {};
            }

            if (
                Object.prototype.hasOwnProperty.call(this.options.map, 'filterTooltip') &&
                typeof this.options.map.filterTooltip == 'object'
            ) {
                if (!Object.prototype.hasOwnProperty.call(this.options.map.filterTooltip, 'active')) {
                    this.options.map.filterTooltip.active = true;
                }

                if (!Object.prototype.hasOwnProperty.call(this.options.map.filterTooltip, 'text')) {
                    this.options.map.filterTooltip.text = 'Совпадения не найдены';
                }

                if (!Object.prototype.hasOwnProperty.call(this.options.map.filterTooltip, 'container')) {
                    this.options.map.filterTooltip.container = `#${this.options.container}`;
                }
            }
        }

        // List
        if (!Object.prototype.hasOwnProperty.call(this.options, 'list')) {
            this.options.list = {};
        }

        if (Object.prototype.hasOwnProperty.call(this.options, 'list') && typeof this.options.list == 'object') {
            if (!Object.prototype.hasOwnProperty.call(this.options.list, 'active')) {
                this.options.list.active = false;
            }

            if (
                Object.prototype.hasOwnProperty.call(this.options.list, 'active') &&
                this.options.list.active &&
                !this.options.list.container
            ) {
                // Если id не передается из параметров, его берём из шаблона
                const $listContainer = this.$block.find('.ylist__list-container');

                if (!$listContainer.length) {
                    throw new Error('You need to set container option in list');
                } else {
                    this.options.list.container = $listContainer.attr('id');
                }
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.list, 'scroll')) {
                this.options.list.scroll = false;
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.list, 'header')) {
                this.options.list.header = true;
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.list, 'clickElement')) {
                this.options.list.clickElement = `.${this.listClassName}__title`;
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.list, 'itemWrapper')) {
                this.options.list.itemWrapper = false;
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.list, 'modifier')) {
                this.options.list.modifier = '';
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.list, 'destroyOnSmall')) {
                this.options.list.destroyOnSmall = false;
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.list, 'dataOrder')) {
                this.options.list.dataOrder = this.options.dataOrder.slice();
            }
        }

        if (!Object.prototype.hasOwnProperty.call(this.options, 'switchContainer')) {
            this.options.switchContainer = false;
        }

        // Cluster
        if (!Object.prototype.hasOwnProperty.call(this.options, 'cluster')) {
            this.options.cluster = {};
        }

        if (Object.prototype.hasOwnProperty.call(this.options, 'cluster') && typeof this.options.cluster == 'object') {
            if (!Object.prototype.hasOwnProperty.call(this.options.cluster, 'icons')) {
                this.options.cluster.icons = ['islands#invertedRedClusterIcons', 'islands#invertedBlueClusterIcons'];
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.cluster, 'inlineStyle')) {
                this.options.cluster.inlineStyle = '';
            }
        }

        // Placemark
        if (!Object.prototype.hasOwnProperty.call(this.options, 'placemark')) {
            this.options.placemark = {};
        }

        if (
            Object.prototype.hasOwnProperty.call(this.options, 'placemark') &&
            typeof this.options.placemark == 'object'
        ) {
            if (!Object.prototype.hasOwnProperty.call(this.options.placemark, 'icons')) {
                this.options.placemark.icons = ['islands#redDotIcon', 'islands#blueDotIcon'];
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.placemark, 'clicked')) {
                this.options.placemark.clicked = true;
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.placemark, 'options')) {
                this.options.placemark.options = {};
            }
        }

        // Balloon
        if (!Object.prototype.hasOwnProperty.call(this.options, 'balloon')) {
            this.options.balloon = {};
        }

        if (Object.prototype.hasOwnProperty.call(this.options, 'balloon') && typeof this.options.balloon == 'object') {
            if (!Object.prototype.hasOwnProperty.call(this.options.balloon, 'activeBeforeBreakpoint')) {
                this.options.balloon.activeBeforeBreakpoint = false;
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.balloon, 'activeAfterBreakpoint')) {
                this.options.balloon.activeAfterBreakpoint = false;
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.balloon, 'closeButton')) {
                this.options.balloon.closeButton = 'x';
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.balloon, 'header')) {
                this.options.balloon.header = true;
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.balloon, 'mapOverflow')) {
                this.options.balloon.mapOverflow = true;
            }

            if (!Object.prototype.hasOwnProperty.call(this.options.balloon, 'modifier')) {
                this.options.balloon.modifier = '';
            }
        }
    }

    /**
     * Инициализация плагина
     */
    initPlugin() {
        this.checkRequiredOptions();

        if (typeof ymaps !== 'undefined') {
            ymaps.ready(() => {
                this.mqlAdaptiveBreakpoint.addListener(() => {
                    this.adaptiveHandle(this.mqlAdaptiveBreakpoint);
                });
                this.adaptiveHandle(this.mqlAdaptiveBreakpoint);

                let timerId;
                $(window).on('resize', () => {
                    clearTimeout(timerId);

                    timerId = setTimeout(() => {
                        if ($(`#${this.options.container}`).hasClass('is-map-hidden')) {
                            // при ресайзе появляется необходимость перезагрузить карту, если она скрыта
                            this.needReloadMap = true;
                        }
                    }, 500);
                });
            });

            if (this.options.list.active) {
                this.createPointsList();
            }
        } else {
            console.error('ymaps is not defined');
        }
    }

    /**
     * Инициализация карты
     */
    initMap() {
        // Если карта уже создана, то дистроим её
        if (this.map) {
            this.map.destroy();
            this.map = null;
            this.placemarks = [];
            this.activePlacemark = null;
            this.clusterer = null;
            this.balloonLayout = null;
        }

        if (this.options.map.dragTooltip.active) {
            this.initMapDragTooltip();
        }

        // Дефолтные опции карты
        const baseMapState = {
            center: this.options.map.center,
            zoom: 13,
            controls: [],
        };
        let extendedMapState = null;

        if (typeof this.options.map.customize == 'object' && typeof this.options.map.customize.state == 'object') {
            extendedMapState = this.setMapState(this.options.map.customize.state, baseMapState);
        }

        // Создаем яндекс карту
        this.map = new ymaps.Map(
            this.options.map.container,
            extendedMapState || baseMapState,
            this.options.map.customize.options
        );

        if (typeof this.options.map.customize == 'object' && typeof this.options.map.customize.controls == 'object') {
            this.setMapControls(this.options.map.customize.controls);
        }

        this.map.behaviors.disable('scrollZoom');

        this.createPlacemarks();

        if (this.options.cluster === false) {
            this.addPlacemarks();
            this.setBoundssetBounds(this.map.geoObjects);
        } else {
            this.createClusterer();
            this.addClusterer();
            this.setBounds(this.clusterer);
        }

        if ((this.touch && this.options.map.drag.disableOnTouch) || this.options.map.drag.disableAlways) {
            this.$block.addClass('is-drag-disable');
            this.map.behaviors.disable('drag');
        }

        // Первый экземпляр коллекции слоев, потом первый слой коллекции
        const layer = this.map.layers.get(0).get(0);

        this.isReadyMap(layer).then(() => {
            const balloonBeforeBreakpoint = this.options.balloon.activeBeforeBreakpoint;
            const balloonAfterBreakpoint = this.options.balloon.activeAfterBreakpoint;
            const $mapContainer = $(`#${this.options.map.container}`);

            if (
                this.activeListItem &&
                ((this.options.placemark.clicked && balloonBeforeBreakpoint && this.isLessThanAdaptiveBreakpoint) ||
                    (this.options.placemark.clicked && balloonAfterBreakpoint && !this.isLessThanAdaptiveBreakpoint))
            ) {
                // Если при инициализации карты есть активный элемент списка
                // и если разрешено отображение балуна
                this.openPlacemarkBalloon(this.activeListItem);
            }

            // Удаляем класс прелоадера и ставится метка о том, что карта загружена
            $mapContainer.removeClass('is-loading');
            this.$block.addClass('is-loaded');

            if (typeof this.options.afterInit == 'function') {
                // Если есть колбек afterInit вызываем его

                this.options.afterInit(this, this.$block);
            }
        });

        // Карта инициализирована
        this.needReloadMap = false;
    }

    /**
     * Дестроит карту
     *
     * @private
     */
    destroyMap() {
        if (this.map) {
            this.map.destroy();
            this.map = null;
            this.placemarks = [];
            this.activePlacemark = null;
            this.clusterer = null;
            this.balloonLayout = null;
        }
    }

    /**
     * Проверка загрузки карты (отрисока и простановка меток)
     *
     * @see https://ru.stackoverflow.com/questions/463638/callback-%D0%B7%D0%B0%D0%B3%D1%80%D1%83%D0%B7%D0%BA%D0%B8-%D0%BA%D0%B0%D1%80%D1%82%D1%8B-yandex-map
     * @param  {Object} layer - слой
     * @return {boolean}       Promise
     * @private
     */
    isReadyMap(layer) {
        function getTileContainer(layerParam) {
            return Object.values(layerParam).find(layerItem => {
                return (
                    layerItem instanceof ymaps.layer.tileContainer.CanvasContainer ||
                    layerItem instanceof ymaps.layer.tileContainer.DomContainer
                );
            });
        }

        return new ymaps.vow.Promise(resolve => {
            const tc = getTileContainer(layer);
            let readyAll = true;
            tc.tiles.each(tile => {
                if (!tile.isReady()) {
                    readyAll = false;
                }
            });

            if (readyAll) {
                resolve();
            } else {
                tc.events.once('ready', () => {
                    resolve();
                });
            }
        });
    }

    /**
     * Инициализация подсказки на карте
     *
     * @private
     */
    initMapDragTooltip() {
        const $container = $(`#${this.options.map.container}`);
        const $dragTooltip = $(`<div class="ylist-drag-tooltip">
                              <span class="ylist-drag-tooltip__text">${this.options.map.dragTooltip.text}</span>
                          </div>`);

        $container.find('.ylist-drag-tooltip').remove();
        $container.append($dragTooltip);

        $container.off('touchmove touchstart touchend touchleave touchcancel');

        if ((this.touch && this.options.map.drag.disableOnTouch) || this.options.map.drag.disableAlways) {
            $container
                .on('touchmove', e => {
                    if (e.originalEvent.touches.length == 1) {
                        $dragTooltip.css('opacity', '1');

                        if (!this.options.balloon.mapOverflow && this.activePlacemark) {
                            // Если балун выходит за пределы карты, то скрываем его при показе подсказки
                            this.activePlacemark.balloon.close();
                        }
                    } else {
                        $dragTooltip.css('opacity', '0');
                    }
                })
                .on('touchstart touchend touchleave touchcancel', () => {
                    $dragTooltip.css('opacity', '0');
                });
        }
    }

    /**
     * Объединяет дефолтные параметры карты state с пользовательскими
     *
     * @param  {Object} customMapState пользовательские параметры карты
     * @param  {Object} baseMapState   дефолтные параметры карты
     * @return {Object} объединенные дефолтные и пользовательские параметры карты
     * @see    https://tech.yandex.ru/maps/doc/jsapi/2.1/ref/reference/Map-docpage/
     * @private
     */
    setMapState(customMapState, baseMapState) {
        const extendedMapState = Object.assign({}, baseMapState, customMapState);

        return extendedMapState;
    }

    /**
     * Добавляет на карту дополнительные контролы, заданные пользователем
     *
     * @param {Array} userControls массив объектов контролов с их настройками
     * @private
     */
    setMapControls(userControls) {
        userControls.forEach(control => {
            const params = {};

            if (!Object.prototype.hasOwnProperty.call(control, 'constructor')) {
                throw new Error(
                    `Нужно указать название метода-конструктора. Например:\nhttps://tech.yandex.ru/maps/doc/jsapi/2.1/ref/reference/control.FullscreenControl-docpage/`
                );
            }

            // Опции элемента управления
            params.options = control.options;

            // Добавляем каждый контрол на карту
            this.map.controls.add(new ymaps.control[control.constructor](params));
        });
    }

    /**
     * Создание массива меток из входящего массива данных
     *
     * @private
     */
    createPlacemarks() {
        this.points.forEach((point, i) => {
            let balloonData;
            const balloonBeforeBreakpoint = this.options.balloon.activeBeforeBreakpoint;
            const balloonAfterBreakpoint = this.options.balloon.activeAfterBreakpoint;

            if (
                (balloonBeforeBreakpoint && balloonAfterBreakpoint && this.options.placemark.clicked) ||
                (balloonBeforeBreakpoint && this.isLessThanAdaptiveBreakpoint && this.options.placemark.clicked) ||
                (balloonAfterBreakpoint && !this.isLessThanAdaptiveBreakpoint && this.options.placemark.clicked)
            ) {
                balloonData = this.setBalloonData(i);
            } else {
                balloonData = {};
            }

            const paramsForSave = {};
            const placemark = new ymaps.Placemark(
                point.coords,
                balloonData,
                this.setPlacemarkOptions(point, i, paramsForSave)
            );

            // Monkey patching
            placemark.id = point.id;
            placemark.events.add('click', e => {
                this.placemarkClickHandler(e);
            });

            // Привязываем иконку к метке
            placemark.icon = paramsForSave.icon;

            // После инициализации обновляем иконку (баг, если иконки разные)
            if (typeof placemark.icon === 'string') {
                placemark.options.set('preset', placemark.icon);
            } else {
                placemark.options.set('iconImageHref', placemark.icon.href);
            }

            if (this.activeListItem && this.activeListItem == point.id) {
                // Подсветка метки если есть активный элемент списка
                if (typeof this.options.placemark.icons[0] == 'string') {
                    placemark.options.set('preset', this.options.placemark.icons[1]);
                } else {
                    placemark.options.set('iconImageHref', this.options.placemark.icons[1].href);
                }

                placemark.isActive = true;
                placemark.options.set('zIndex', 1000);
            }

            this.placemarks.push(placemark);
        });
    }

    /**
     * Добавление всех меток на карту
     *
     * @private
     */
    addPlacemarks() {
        this.placemarks.forEach(placemark => {
            this.map.geoObjects.add(placemark);
        });
    }

    /**
     * Открывает быллун метки
     *
     * @param {string} placemarkId id метки
     * @private
     */
    openPlacemarkBalloon(placemarkId) {
        const currentPlacemark = this.placemarks.find(placemark => placemark.id == placemarkId);

        if (currentPlacemark) {
            currentPlacemark.events.fire('click');
        }
    }

    /**
     * Возвращает объект, содержащий опции метки.
     *
     * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/GeoObject.xml
     * @private
     * @param {Object} item - метка из массива точек
     * @param {number} index - индекс элемента массива
     * @param {Object} paramsForSave - параметры, которые необходимо будет передать
     */
    setPlacemarkOptions(item, index, paramsForSave) {
        const placemarkOptions = this.options.placemark.options;
        const balloonBeforeBreakpoint = this.options.balloon.activeBeforeBreakpoint;
        const balloonAfterBreakpoint = this.options.balloon.activeAfterBreakpoint;
        const iconGetter = this.options.placemark.icons[0];

        paramsForSave.icon = iconGetter;

        // Задаем стандартную иконку метки из набора яндекса
        const makePreset = iconGetterParam => {
            placemarkOptions.preset = iconGetterParam;
        };

        // Создаем кастомную иконку
        const makeCustomIcon = iconGetterParam => {
            // Необходимо указать данный тип макета.
            placemarkOptions.iconLayout = 'default#image';
            // Своё изображение иконки метки.
            placemarkOptions.iconImageHref = iconGetterParam.href;
            // Размеры метки.
            placemarkOptions.iconImageSize = iconGetterParam.size;
            // Смещение левого верхнего угла иконки относительно
            // её "ножки" (точки привязки).
            placemarkOptions.iconImageOffset = iconGetterParam.offset;
        };

        placemarkOptions.hasBalloon = false;

        if (typeof iconGetter === 'string') {
            // Задаем стандартную иконку метки из набора яндекса
            makePreset(iconGetter);
        } else if (typeof iconGetter === 'object') {
            // Если задаем кастомную иконку метки
            makeCustomIcon(iconGetter);
        } else if (typeof iconGetter === 'function') {
            // Если функция, запускаем её
            const icon = iconGetter(item, index);

            if (typeof icon === 'string') {
                // Задаем стандартную иконку метки из набора яндекса
                makePreset(icon);
            } else if (typeof icon === 'object') {
                // Если задаем кастомную иконку метки
                makeCustomIcon(icon);
            }

            paramsForSave.icon = icon;
        }

        if (
            (balloonBeforeBreakpoint && balloonAfterBreakpoint && this.options.placemark.clicked) ||
            (balloonBeforeBreakpoint && this.isLessThanAdaptiveBreakpoint && this.options.placemark.clicked) ||
            (balloonAfterBreakpoint && !this.isLessThanAdaptiveBreakpoint && this.options.placemark.clicked)
        ) {
            placemarkOptions.hasBalloon = true;
            placemarkOptions.balloonLayout = this.createBalloonLayout();
            placemarkOptions.balloonContentLayout = this.createBalloonContentLayout();
            placemarkOptions.balloonAutoPan = false;
            placemarkOptions.balloonShadow = false;
            placemarkOptions.balloonPanelMaxMapArea = 0;
        }

        if (!this.options.placemark.clicked) {
            placemarkOptions.cursor = 'default';
        }

        return placemarkOptions;
    }

    /**
     * Создание кластера из массива меток
     *
     * @private
     */
    createClusterer() {
        if (this.clusterer) {
            this.clusterer.removeAll();
        }

        /**
         * Создадим кластеризатор, вызвав функцию-конструктор.
         * Список всех опций доступен в документации.
         * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/Clusterer.xml#constructor-summary
         */
        this.clusterer = new ymaps.Clusterer({
            /**
             * Ставим true, если хотим кластеризовать только точки с одинаковыми координатами.
             */
            groupByCoordinates: false,
            /**
             * Опции кластеров указываем в кластеризаторе с префиксом "cluster".
             * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/ClusterPlacemark.xml
             */
            clusterDisableClickZoom: false,
            clusterHideIconOnBalloonOpen: false,
            geoObjectHideIconOnBalloonOpen: false,
            zoomMargin: [80, 40, 40, 40],
        });

        if (typeof this.options.cluster.icons[0] == 'string') {
            // Если задаем стандартную иконку кластера из набора яндекса
            this.clusterer.options.set({
                /**
                 * Через кластеризатор можно указать только стили кластеров,
                 * стили для меток нужно назначать каждой метке отдельно.
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/option.presetStorage.xml
                 */
                preset: this.options.cluster.icons[0],
            });
        } else {
            // Если задаем для кластера кастомную иконку

            // Сделаем макет содержимого иконки кластера
            const MyClustererIconContentLayout = ymaps.templateLayoutFactory.createClass(
                `<div style="${this.options.cluster.inlineStyle}">{{ properties.geoObjects.length }}</div>`
            );

            this.clusterer.options.set({
                clusterIcons: this.options.cluster.icons[0],
                // Эта опция отвечает за размеры кластеров.
                // В данном случае для кластеров, содержащих до 100 элементов,
                // будет показываться маленькая иконка. Для остальных - большая.
                clusterNumbers: [100],
                clusterIconContentLayout: MyClustererIconContentLayout,
            });
        }

        /**
         * В кластеризатор можно добавить javascript-массив меток (не геоколлекцию) или одну метку.
         * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/Clusterer.xml#add
         */
        this.clusterer.add(this.placemarks);
    }

    /**
     * Добавление кластера на карту
     *
     * @private
     */
    addClusterer() {
        this.map.geoObjects.add(this.clusterer);
    }

    /**
     * Создание макета балуна на основе фабрики макетов с помощью текстового шаблона
     *
     * @private
     */
    createBalloonLayout() {
        const ylistInstance = this;

        const balloonLayout = ymaps.templateLayoutFactory.createClass(
            `<div class="ylist-balloon ${this.options.balloon.modifier}">
                <button class="ylist-balloon__close" type="button">${this.options.balloon.closeButton}</button>
                <div class="ylist-balloon__inner">
                    $[[options.contentLayout]]
                </div>
            </div>`,
            {
                /**
                 * Строит экземпляр макета на основе шаблона и добавляет его в родительский HTML-элемент.
                 *
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/layout.templateBased.Base.xml#build
                 */
                build() {
                    this.constructor.superclass.build.call(this);
                    this.$element = $('.ylist-balloon', this.getParentElement());
                    this.applyElementOffset();
                    this.$element.find('.ylist-balloon__close').on('click', $.proxy(this.onCloseClick, this));

                    ylistInstance.balloonParams.balloonWidth = this.$element[0].offsetWidth;
                    ylistInstance.balloonParams.balloonHeight =
                        this.$element[0].offsetHeight + ylistInstance.balloonParams.balloonTailHeight;
                },

                /**
                 * Удаляет содержимое макета из DOM.
                 *
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/layout.templateBased.Base.xml#clear
                 */
                clear() {
                    this.$element.find('.ylist-balloon__close').off('click');
                    this.constructor.superclass.clear.call(this);
                },

                /**
                 * Метод будет вызван системой шаблонов АПИ при изменении размеров вложенного макета.
                 *
                 * @param {Object} sublayoutInfo - Информация о вложенном макете.
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/IBalloonLayout.xml#event-userclose
                 */
                onSublayoutSizeChange(...args) {
                    this.balloonLayout.superclass.onSublayoutSizeChange.call(this, ...args);

                    if (!this.isElement(this.$element)) {
                        return;
                    }

                    this.applyElementOffset();

                    this.events.fire('shapechange');
                },

                /**
                 * Сдвигаем балун, чтобы "хвостик" указывал на точку привязки.
                 *
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/IBalloonLayout.xml#event-userclose
                 */
                applyElementOffset() {
                    const left = `${-(this.$element[0].offsetWidth / 2)}px`;
                    const top = `${-(this.$element[0].offsetHeight + ylistInstance.balloonParams.balloonTailHeight)}px`;

                    this.$element.css('left', left);
                    this.$element.css('top', top);
                },

                /**
                 * Закрывает балун при клике на крестик, кидая событие "userclose" на макете.
                 *
                 * @param {Object} e - событие
                 *
                 * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/IBalloonLayout.xml#event-userclose
                 */
                onCloseClick(e) {
                    e.preventDefault();

                    this.events.fire('userclose');
                },

                /**
                 * Проверяем наличие элемента (в ИЕ и Опере его еще может не быть).
                 *
                 * @param {jQuery} [element] Элемент.
                 * @return {boolean} Флаг наличия.
                 */
                _isElement(element) {
                    return element && element[0] && element.find('.ylist-balloon__inner')[0];
                },
            }
        );

        return balloonLayout;
    }

    /**
     * Создание вложенного макета содержимого балуна
     *
     * @private
     */
    createBalloonContentLayout() {
        let balloonContentLayout = ``;

        if (this.options.balloon.header === false) {
            balloonContentLayout = `<div class="ylist-balloon__content">$[properties.balloonContent]</div>`;
        } else {
            balloonContentLayout = `<h3 class="ylist-balloon__title">$[properties.balloonHeader]</h3>
                                    <div class="ylist-balloon__content">$[properties.balloonContent]</div>`;
        }

        return ymaps.templateLayoutFactory.createClass(balloonContentLayout);
    }

    /**
     * Возвращает объект, содержащий данные метки.
     *
     * @param {number} index - индекс элемента массива точек
     *
     * @see https://api.yandex.ru/maps/doc/jsapi/2.1/ref/reference/GeoObject.xml
     * @private
     */
    setBalloonData(index) {
        let balloonHeader = ``;
        let balloonContent = ``;

        this.options.dataOrder.forEach(dataOptionName => {
            let optionName = ``;
            let optionContent = ``;
            const hasOwnProperty = Object.prototype.hasOwnProperty.call(this.options.dataExtension, dataOptionName);

            if (hasOwnProperty && dataOptionName != 'name') {
                // Формируется контент одной опции из колбека
                optionContent = this.options.dataExtension[dataOptionName](
                    this.points[index][dataOptionName],
                    this.points[index]
                );
            } else if (hasOwnProperty && dataOptionName == 'name') {
                // Формируется контент заголовка опции из колбека
                optionName = this.options.dataExtension[dataOptionName](
                    this.points[index][dataOptionName],
                    this.points[index]
                );
            } else {
                // Контент опции передается как есть если колбек для неё не задан
                optionName = this.points[index].name;
                optionContent = dataOptionName != 'name' ? this.points[index][dataOptionName] : '';
            }

            balloonHeader += optionName;
            balloonContent += optionContent;
        });

        return {
            balloonHeader,
            balloonContent,
        };
    }

    /**
     * Балун, выходящий за пределы карты
     *
     * @param {Object} map - объект карты
     * @param {Object} placemark - метка
     * @param {Object} mapData - данные о перемещении
     *
     * @see https://tech.yandex.ru/maps/jsapi/doc/2.1/ref/reference/Map-docpage/#event_detail__event-actiontick
     * @private
     */
    setBalloonPane(map, placemark, mapData) {
        mapData = mapData || {
            globalPixelCenter: map.getGlobalPixelCenter(),
            zoom: map.getZoom(),
        };

        const mapSize = map.container.getSize();
        const mapBounds = [
            [mapData.globalPixelCenter[0] - mapSize[0] / 2, mapData.globalPixelCenter[1] - mapSize[1] / 2],
            [mapData.globalPixelCenter[0] + mapSize[0] / 2, mapData.globalPixelCenter[1] + mapSize[1] / 2],
        ];
        const balloonPosition = placemark.balloon.getPosition();
        // Используется при изменении зума.
        const zoomFactor = 2 ** (mapData.zoom - map.getZoom());
        // Определяем, попадает ли точка привязки балуна в видимую область карты.
        const pointInBounds = ymaps.util.pixelBounds.containsPoint(mapBounds, [
            balloonPosition[0] * zoomFactor,
            balloonPosition[1] * zoomFactor,
        ]);
        const isInOutersPane = placemark.options.get('balloonPane') == 'outerBalloon';

        if (!pointInBounds && isInOutersPane) {
            // Если точка привязки не попадает в видимую область карты, переносим балун во внутренний контейнер
            placemark.options.set({
                balloonPane: 'balloon',
                balloonShadowPane: 'shadows',
            });
        } else if (pointInBounds && !isInOutersPane) {
            // и наоборот.
            placemark.options.set({
                balloonPane: 'outerBalloon',
                balloonShadowPane: 'outerBalloon',
            });
        }
    }

    /**
     * Создает DOM элемент списка
     *
     * @param  {Array}   point данные точки из входящего json
     * @return {Element}       DOM элемент спсика с содержимым
     * @private
     */
    createListElement(point) {
        let $elementTitle = $('<h3/>', { class: `${this.listClassName}__title` });
        let $elementContent = ``;
        let $elementWrapper = ``;

        const $listElement = $('<li/>', {
            id: point.id,
            class: `${this.listClassName}__item`,
        });

        if (point.name && this.options.list.header) {
            if (Object.prototype.hasOwnProperty.call(this.options.dataExtension, 'name')) {
                // Формируется контент одной опции из колбека
                $elementTitle.html(this.options.dataExtension.name(point.name, point));
            } else {
                // Контент опции передается как есть если колбек для неё не задан
                $elementTitle.html(point.name);
            }
        } else {
            $elementTitle = null;
        }

        if (!Array.isArray(this.options.list.dataOrder)) {
            throw new TypeError('options.list.dataOrder должен быть массивом');
        }

        this.options.list.dataOrder.forEach(dataOptionName => {
            let optionContent = ``;

            if (
                Object.prototype.hasOwnProperty.call(this.options.dataExtension, dataOptionName) &&
                dataOptionName != 'name'
            ) {
                // Формируется контент одной опции из колбека
                optionContent = this.options.dataExtension[dataOptionName](point[dataOptionName], point);
            } else if (dataOptionName != 'name') {
                // Контент опции передается как есть если колбек для неё не задан
                optionContent = point[dataOptionName];
            }

            $elementContent += optionContent;
        });

        if (this.options.list.itemWrapper !== false) {
            // Оборачиваем все содержимое элемента списка в указанную в опциях обертку
            $elementWrapper = $('<div/>', { class: this.options.list.itemWrapper });

            $elementWrapper.append($elementTitle, $elementContent);
            $listElement.append($elementWrapper);
        } else {
            $listElement.append($elementTitle, $elementContent);
        }

        return $listElement;
    }

    /**
     * Создает элемент список, наполняет его содержимым и добавляет в DOM
     *
     * @private
     */
    createPointsList() {
        const $list = $('<ul/>', { class: `${this.listClassName} ${this.options.list.modifier}` });

        this.points.forEach(point => {
            $list.append(this.createListElement(point));
        });

        $(`#${this.options.list.container}`)
            .html('')
            .append($list);

        // При клике на элемент списка, срабатывает соответстующая точка на карте
        $(document).on('click', `${this.options.list.clickElement}`, e => {
            const listItemId = $(e.target)
                .closest(`.${this.listClassName}__item`)
                .attr('id');

            if (this.placemarks.length > 0) {
                // Если карта еще не инициализирована
                for (let i = 0; i < this.placemarks.length; i += 1) {
                    const placemark = this.placemarks[i];

                    if (placemark.id == listItemId) {
                        this.listItemClickHandler(e, placemark);
                        break;
                    }
                }
            } else {
                this.listItemClickHandler(e, listItemId);
            }
        });
    }

    /**
     * Масштабирование карты так, чтобы были видны все объекты
     *
     * @param {Object} objects массив геобъектов или кластер
     * @private
     * @return {Promise} Возращает промис (всегда resolve)
     */
    setBounds(objects) {
        if (typeof this.placemarks === 'object' && this.placemarks.length === 1) {
            // Если метка 1, то её координаты ставятся центром карты и масштаб не самый максимальный
            return this.map.setCenter(this.placemarks[0].geometry.getCoordinates(), 16);
        }

        return this.map.setBounds(objects.getBounds(), {
            checkZoomRange: true,
            zoomMargin: [80, 40, 40, 40],
        });
    }

    /**
     * Обработчик клика на метку
     *
     * @param {Object} e    event
     * @private
     */
    placemarkClickHandler(e) {
        if (!this.options.placemark.clicked) {
            return;
        }

        const placemark = e.get('target');
        const balloonBeforeBreakpoint = this.options.balloon.activeBeforeBreakpoint;
        const balloonAfterBreakpoint = this.options.balloon.activeAfterBreakpoint;

        this.activePlacemark = placemark;

        this.commonClickHandler(placemark);

        if (
            (balloonBeforeBreakpoint && balloonAfterBreakpoint) ||
            (balloonBeforeBreakpoint && !balloonAfterBreakpoint && this.isLessThanAdaptiveBreakpoint) ||
            (!balloonBeforeBreakpoint && balloonAfterBreakpoint && !this.isLessThanAdaptiveBreakpoint)
        ) {
            // Настройка балуна, выходящего за пределы карты
            if (this.options.balloon.mapOverflow === false) {
                const outerHandler = event => {
                    if (placemark.options.get('balloonPane') === 'outerBalloon') {
                        this.setBalloonPane(this.map, placemark, event.get('tick'));
                    }
                };
                const innerHandler = event => {
                    if (placemark.options.get('balloonPane') !== 'outerBalloon') {
                        this.setBalloonPane(this.map, placemark, event.get('tick'));
                    }
                };

                // При открытии балуна начинаем слушать изменение центра карты. Вызываем функцию в двух случаях:
                this.map.geoObjects.events.add('balloonopen', () => {
                    // 1) в начале движения (если балун во внешнем контейнере);
                    this.map.events.add('actiontick', outerHandler);
                    // 2) в конце движения (если балун во внутреннем контейнере).
                    this.map.events.add('actiontickcomplete', innerHandler);
                    // Сразу делаем проверку на позицию балуна
                    this.setBalloonPane(this.map, placemark);
                });

                // При закрытии балуна удаляем слушатели.
                this.map.geoObjects.events.add('balloonclose', () => {
                    this.map.events.remove('actiontick', outerHandler);
                    this.map.events.remove('actiontickcomplete', innerHandler);
                });
            }

            /**
             * Расчитывает координаты центра, с учетом размеров балуна,
             * и центрирует карту относительно балуна
             */
            const setBalloonToCenter = () => {
                // Если балун выходит за рамки карты, опустим балун на 1/4 его высоты
                const divider = this.options.balloon.mapOverflow === false ? 4 : 2;

                const coords = this.map.options
                    .get('projection')
                    .toGlobalPixels(placemark.geometry.getCoordinates(), this.map.getZoom());

                // Сдвигаем координаты на половину высоты балуна
                coords[1] -= this.balloonParams.balloonHeight / divider;

                const newCoords = this.map.options.get('projection').fromGlobalPixels(coords, this.map.getZoom());

                this.map.panTo(newCoords, { flying: true });

                // После выполнения функции удаляем обработчик
                this.map.geoObjects.events.remove('balloonopen', setBalloonToCenter);
            };

            this.map.geoObjects.events.add('balloonopen', setBalloonToCenter);
        } else {
            this.map.panTo(placemark.geometry.getCoordinates(), { flying: true });
        }
    }

    /**
     * Обработчик клика на элемент списка
     *
     * @param {Object} e         event
     * @param {Object} placemark объект метки или id
     * @private
     */
    listItemClickHandler(e, placemark) {
        this.commonClickHandler(placemark);

        const balloonBeforeBreakpoint = this.options.balloon.activeBeforeBreakpoint;
        const balloonAfterBreakpoint = this.options.balloon.activeAfterBreakpoint;

        if (typeof placemark !== 'string') {
            if (this.activePlacemark && this.map.getZoom() < 11) {
                const prevClustered = this.clusterer.getObjectState(this.activePlacemark).isClustered;
                const currentClustered = this.clusterer.getObjectState(placemark).isClustered;

                // Если оба элемента на небольшом зуме не кластеризованы, просто подвинем карту к ним
                if (!prevClustered && !currentClustered) {
                    this.map.panTo(placemark.geometry.getCoordinates(), { flying: true });

                    this.activePlacemark = placemark;

                    if (
                        this.options.list.active &&
                        this.options.placemark.clicked &&
                        balloonAfterBreakpoint &&
                        !this.isLessThanAdaptiveBreakpoint
                    ) {
                        // Диспатчим метку только после брейкпоинта при активном списке
                        placemark.events.fire('click');
                    }
                    return;
                }
            }

            // Устанавливаем минимальное значение зума, при котором активная метка находится вне кластера
            let zoom = this.map.getZoom() === 9 ? this.map.getZoom() : 9;

            do {
                this.map.setCenter(placemark.geometry.getCoordinates(), zoom);
                zoom += 1;
            } while (this.clusterer.getObjectState(placemark).isClustered);

            this.activePlacemark = placemark;

            if (
                (this.options.placemark.clicked && balloonBeforeBreakpoint && balloonAfterBreakpoint) ||
                (this.options.placemark.clicked &&
                    balloonBeforeBreakpoint &&
                    !balloonAfterBreakpoint &&
                    this.isLessThanAdaptiveBreakpoint) ||
                (this.options.placemark.clicked &&
                    !balloonBeforeBreakpoint &&
                    balloonAfterBreakpoint &&
                    !this.isLessThanAdaptiveBreakpoint)
            ) {
                placemark.events.fire('click');
            }
        }
    }

    /**
     * Общий обработчик клика на метку и на элемент списка
     *
     * @param {Object} placemark объект метки или id
     * @private
     */
    commonClickHandler(placemark) {
        let $listContainer = null;
        let $listItem = null;
        let activeListItemId = null;
        const listActive = this.options.list.active;

        if (listActive) {
            $listContainer = $(`#${this.options.list.container}`);
        }

        if (typeof placemark == 'string') {
            if (listActive) {
                $listItem = $(`#${placemark}`);
            }
            activeListItemId = placemark;
        } else {
            if (listActive) {
                $listItem = $(`#${placemark.id}`);
            }
            activeListItemId = placemark.id;

            // Возвращаем всем меткам и кластерам исходный вид
            this.placemarks.forEach(placemarkParam => {
                if (typeof placemarkParam.icon === 'string') {
                    placemarkParam.options.set('preset', placemarkParam.icon);
                } else {
                    placemarkParam.options.set('iconImageHref', placemarkParam.icon.href);
                }

                if (placemarkParam.balloon && placemarkParam.balloon.close) {
                    placemarkParam.balloon.close();
                }

                if (this.clusterer.getObjectState(placemarkParam).cluster) {
                    if (typeof this.options.cluster.icons[0] == 'string') {
                        this.clusterer
                            .getObjectState(placemarkParam)
                            .cluster.options.set('preset', this.options.cluster.icons[0]);
                    } else {
                        this.clusterer
                            .getObjectState(placemarkParam)
                            .cluster.options.set('clusterIcons', this.options.cluster.icons[0]);
                    }
                }

                placemarkParam.isActive = false;
                placemarkParam.options.set('zIndex', 650); // 650 дефолтное значение
            });

            placemark.options.set('zIndex', 1000);

            // Если метка в кластере, соответствующий кластер будет подсвечен
            if (this.clusterer.getObjectState(placemark).isClustered) {
                if (typeof this.options.cluster.icons[0] == 'string') {
                    this.clusterer
                        .getObjectState(placemark)
                        .cluster.options.set('preset', this.options.cluster.icons[1]);
                } else {
                    this.clusterer
                        .getObjectState(placemark)
                        .cluster.options.set('clusterIcons', this.options.cluster.icons[1]);
                }
            }

            // Подсветка метки на карте
            if (typeof this.options.placemark.icons[0] == 'string') {
                placemark.options.set('preset', this.options.placemark.icons[1]);
            } else {
                placemark.options.set('iconImageHref', this.options.placemark.icons[1].href);
            }

            placemark.isActive = true;
        }

        this.activeListItem = activeListItemId;

        if (listActive) {
            // Подсветка элемента списка
            $listContainer.find(`.${this.listClassName}__item.is-active`).removeClass('is-active');
            $listItem.addClass('is-active');

            // Скроллим список к нужному элементу
            if (typeof this.options.list.scroll == 'boolean' && !this.options.list.scroll) {
                const $list = $listContainer.find('.ylist-list');
                $list.parent().scrollTop($listItem.get(0).offsetTop);
            } else {
                this.options.list.scroll($listContainer, $listItem);
            }
        }
    }

    /**
     * Добавляем CSS-класс для показа карты
     */
    setMapVisible = () => {
        $(`#${this.options.container}`).removeClass('is-map-hidden');
        $(`#${this.options.container}`).addClass('is-map-visible');
    };

    /**
     * Добавляем CSS-класс для скрытия карты
     *
     * @param {Object} $switchContainerElem - jQuery-блок, контайнер
     */
    setMapHidden = $switchContainerElem => {
        $switchContainerElem.find('[data-ylist-switch]').removeClass('is-active');
        $switchContainerElem.find('[data-ylist-switch="list"]').addClass('is-active');
        $(`#${this.options.container}`).removeClass('is-map-visible');
        $(`#${this.options.container}`).addClass('is-map-hidden');
    };

    /**
     * Обработчик перехода через разрешения через adaptiveBreakpoint
     *
     * @param {Object} mql  MediaQueryList
     * @private
     */
    adaptiveHandle(mql) {
        const listActive = this.options.list.active;
        const destroyListOnSmall = this.options.list.destroyOnSmall;
        const { switchContainer } = this.options;
        // switch может прийти как строкой (id), так и объектом (switch = {id, type})
        this.switchId = (() => {
            const getIdFromTemplate = () => {
                // Если id не передается из параметров, его берём из шаблона
                const $switchContainer = $(this.$block.attr('data-switch'));

                if (!$switchContainer.length) {
                    console.error(
                        'Компонент-переключатель не найден. Укажите его id в параметр switchContainer или воспользуйтесь дефолтным шаблоном'
                    );
                }

                return $switchContainer.attr('id');
            };

            if (!listActive && !switchContainer) return null;

            if (typeof switchContainer === 'string') {
                return switchContainer;
            }
            if (typeof switchContainer === 'object') {
                if (switchContainer.id) {
                    return switchContainer.id;
                }

                // Если id не указан, берем из шаблона
                return getIdFromTemplate();
            }

            // Если id не указан, берем из шаблона
            return getIdFromTemplate();
        })();
        const switchType = typeof switchContainer === 'object' ? switchContainer.type : !!switchContainer;

        // Добавляем обработчик клика на элементы переключения
        $(document).on('click', `#${this.switchId} [data-ylist-switch]`, this.switchHandler);

        if (mql.matches) {
            // Переключение с десктопа на мобильные устройства
            let $switchContainerElem;

            if (switchContainer) {
                $switchContainerElem = $(`#${this.switchId}`);
                // Показываем блок с кнопками
                $switchContainerElem.addClass('is-visible');
                $switchContainerElem.find('[data-ylist-switch]').removeClass('is-active');
                $switchContainerElem.find('[data-ylist-switch="list"]').addClass('is-active');
            }

            this.isLessThanAdaptiveBreakpoint = true;

            if (listActive) {
                this.destroyMap();
            }

            if (switchContainer !== false) {
                if (listActive && !destroyListOnSmall) {
                    this.setMapHidden($switchContainerElem);
                }
            }

            $(`#${this.options.map.container}`).addClass('is-adaptive');
            $(`#${this.options.list.container}`).addClass('is-adaptive');
            $(`#${this.options.container}`).addClass('is-adaptive');

            if ((!this.map && !listActive) || destroyListOnSmall) {
                // Если список отключен и карта не инициализирована, то инитим карту на адаптиве сразу
                this.initMap();
            }
        } else {
            // Переключение с мобильного устройства на десктоп

            const $switchContainerElem = $(`#${this.switchId}`);

            this.isLessThanAdaptiveBreakpoint = false;

            if (switchContainer != false && switchType !== 'always') {
                // Скрываем блок с кнопками на десктопе
                $switchContainerElem.removeClass('is-visible');
                $switchContainerElem.find('[data-ylist-switch]').removeClass('is-active');
            } else if (switchType === 'always') {
                // Показываем блок с кнопками
                $switchContainerElem.addClass('is-visible');
                $switchContainerElem.find('[data-ylist-switch]').removeClass('is-active');
                $switchContainerElem.find('[data-ylist-switch="map"]').addClass('is-active');
            }

            $(`#${this.options.container}`).removeClass('is-map-hidden is-map-visible');
            $(`#${this.options.map.container}`).removeClass('is-adaptive');
            $(`#${this.options.list.container}`).removeClass('is-adaptive');
            $(`#${this.options.container}`).removeClass('is-adaptive');

            if (this.map) {
                this.destroyMap();
            }

            this.initMap();

            if (this.currentFilterCallback && this.currentFilterParam) {
                // Если производилась фильтрация и карта переинициализируется,
                // то надо еще раз вызвать фильтрацию, чтобы метки карты тоже отфильтровались
                this.filter(this.currentFilterCallback, this.currentFilterParam);
            }

            if (switchContainer != false && switchType !== 'always') {
                // Удаляем обработчик клика на элементы переключения
                $(document).off('click', `#${this.switchId} [data-ylist-switch]`, this.switchHandler);
            }
        }

        if (this.map) {
            if ((this.touch && this.options.map.drag.disableOnTouch) || this.options.map.drag.disableAlways) {
                this.map.behaviors.disable('drag');
                this.$block.addClass('is-drag-disable');
            } else {
                this.map.behaviors.enable('drag');
                this.$block.removeClass('is-drag-disable');
            }

            if (this.options.map.dragTooltip.active) {
                this.initMapDragTooltip();
            }
        }
    }

    /**
     * Обработчик переключения карта-список на разрешении <adaptiveBreakpoint
     *
     * @param {Object} e    event
     * @private
     */
    switchHandler = e => {
        const $elem = $(e.target).closest('[data-ylist-switch]');
        const balloonBeforeBreakpoint = this.options.balloon.activeBeforeBreakpoint;

        // const balloonAfterBreakpoint = this.options.balloon.activeAfterBreakpoint; - данное значение пока не используется

        const $switchContainerElem = $(`#${this.switchId}`);

        if (!$elem.length || $elem.hasClass('is-active')) {
            return;
        }

        if ($elem.attr('data-ylist-switch') === 'map') {
            this.setMapVisible();

            if (this.needReloadMap) {
                this.initMap();

                if (this.currentFilterCallback && this.currentFilterParam) {
                    // Если производилась фильтрация списка пока карта не была инициализирована,
                    // то надо еще раз вызвать фильтрацию, чтобы метки карты тоже отфильтровались
                    this.filter(this.currentFilterCallback, this.currentFilterParam);
                }
            } else if (
                this.options.list.active &&
                this.activeListItem &&
                this.options.placemark.clicked &&
                balloonBeforeBreakpoint &&
                this.isLessThanAdaptiveBreakpoint
            ) {
                // Если активный элемент списка
                // и если разрешено отображение балуна до брейкпоинта
                this.openPlacemarkBalloon(this.activeListItem);
            }
        } else if ($elem.attr('data-ylist-switch') === 'list') {
            this.setMapHidden($switchContainerElem);
        }

        $(`#${this.switchId} [data-ylist-switch]`).removeClass('is-active');
        $elem.addClass('is-active');
    };

    /**
     * Публичный метод, реализующий фильтрацию
     *
     * @param {Function}         callback колбек с условиями фильтрации
     * @param {(string|boolean)} param    параметр, по которому происходит фильтрация
     * @public
     */
    filter(callback, param) {
        if (typeof callback !== 'function') {
            throw new TypeError('Аргумент должен быть функцией');
        }

        // Запоминаем колбек
        this.currentFilterCallback = callback;

        const { points } = this;
        const { placemarks } = this;
        let falseFilterCounter = 0;
        const filterTooltipContainer = this.options.map.filterTooltip.container;
        const $filterTooltip = $(`<div class="ylist-filter-tooltip">
                                    <span class="ylist-filter-tooltip__text">${this.options.map.filterTooltip.text}</span>
                                </div>`);

        if (this.map && !placemarks.length) {
            console.warn('Невозможно запустить фильтрацию. Массив меток пуст.');
            return;
        }

        // Скрываем все
        if (this.map) {
            placemarks.forEach(placemarkItem => {
                placemarkItem.options.set('visible', false);
                if (this.clusterer) {
                    this.clusterer.remove(placemarkItem);
                }
                $(`#${this.options.container} #${placemarkItem.id}`).hide();
            });
        } else {
            $(`#${this.options.list.container} .${this.listClassName}__item`).hide();
        }

        points.forEach((dataItem, i) => {
            if (callback(dataItem, i, points)) {
                // Показываем нужное
                if (this.map) {
                    placemarks[i].options.set('visible', true);
                    if (this.clusterer) {
                        this.clusterer.add(placemarks[i]);
                    }
                }

                $(`#${dataItem.id}`).show();

                // Запоминаем значение, по которому была успешная фильтрация
                this.currentFilterParam = param;
            } else {
                falseFilterCounter += 1;
            }
        });

        if (falseFilterCounter == points.length) {
            // Нет совпадений
            if (this.options.map.filterTooltip.active) {
                $(`${filterTooltipContainer} .ylist-filter-tooltip`).remove();
                $(filterTooltipContainer).append($filterTooltip);
                $(`${filterTooltipContainer} .ylist-filter-tooltip`).css('opacity', '1');
            } else {
                console.warn(this.options.map.filterTooltip.text);
            }
        } else {
            if (this.options.map.filterTooltip.active) {
                $(`${filterTooltipContainer} .ylist-filter-tooltip`).remove();
            }

            // Устанавливаем зум после фильтрации
            const zoom = 16;
            const setZoom = () => this.map.setZoom(zoom);

            // Масштабируем карту так, чтобы были видны все метки
            if (typeof this.options.cluster == 'boolean' && !this.options.cluster) {
                this.setBounds(this.map.geoObjects).then(setZoom);
            } else {
                this.setBounds(this.clusterer);
            }
        }
    }

    /**
     * Сбрасывает результат фильтрации и показывает полный список меток
     *
     * @public
     */
    clearFilter() {
        const filterTooltipContainer = this.options.map.filterTooltip.container;

        // Сбрасываем колбек
        this.currentFilterCallback = null;
        // Сбрасываем параметр фильтрации
        this.currentFilterParam = null;

        if (this.options.map.filterTooltip.active) {
            // Удаляем тултип
            $(`${filterTooltipContainer} .ylist-filter-tooltip`).remove();
        }

        const { points } = this;
        const { placemarks } = this;

        for (let i = 0; i < points.length; i += 1) {
            if (this.map) {
                placemarks.forEach(placemarkItem => {
                    placemarkItem.options.set('visible', true);
                    if (this.clusterer) {
                        this.clusterer.add(placemarkItem);
                    }
                    $(`#${placemarkItem.id}`).show();
                });

                // Масштабируем карту так, чтобы были видны все метки
                if (typeof this.options.cluster == 'boolean' && !this.options.cluster) {
                    this.setBounds(this.map.geoObjects);
                } else {
                    this.setBounds(this.clusterer);
                }
            } else {
                $(`#${this.options.list.container} .${this.listClassName}__item`).show();
            }
        }
    }

    /**
     * Получает и возвращает объект с данными метки c {id}
     *
     * @param  {string} id  id метки в формате "#some-id" или "some-id"
     * @return {Object}     данные метки
     * @public
     */
    getPointData(id) {
        let point = null;
        const { points } = this;

        // Можно передавать id как с решеткой, так и без решетки
        id = id.replace(/^#/, '');

        for (let i = 0; i < points.length; i += 1) {
            if (points[i].id === id) {
                point = points[i];
                break;
            }
        }

        return point;
    }

    /**
     * Реинициализация карты, опционально можно передать новые данные меток
     *
     * @param {Array} newData массив объектов см. описание options.data в readme
     * @public
     */
    update(newData) {
        this.destroyMap();

        if (newData) {
            this.points = $.extend(true, [], newData);
        }

        this.init();
    }

    /**
     * Обновляет список меток.
     */
    updatePointsList() {
        this.createPointsList();
    }
}
